/**
 * useFormSchema — fetch a category's resolved form-schema for the seller form
 * renderer (PIM Wave P2, Task 6).
 *
 * Hits the PUBLIC, unauthenticated `GET /marketplace/categories/{id}/form-schema`
 * (the exact same contract the admin P1 preview consumes — zero drift by
 * construction). Cached per category id. 404 (`catalog_schema.category.
 * not_found`) / 409 (`catalog_schema.category.archived`) surface as an
 * `ApiError` the editor maps to a "pick another category" message.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";
import type { FormSchemaResponse } from "@/features/seller-dashboard/attributes/formSchema";

export function formSchemaQueryKey(categoryId: string): readonly unknown[] {
  return ["marketplace", "form-schema", categoryId];
}

export function getFormSchema(categoryId: string): Promise<FormSchemaResponse> {
  return apiClient.get<FormSchemaResponse>(
    `/marketplace/categories/${categoryId}/form-schema`,
  );
}

/**
 * Query the form-schema for `categoryId`. Disabled while `categoryId` is null
 * or "" (no primary category chosen yet). The schema changes rarely — cached
 * for 60s, matching the category-tree budget.
 */
export function useFormSchema(
  categoryId: string | null,
): UseQueryResult<FormSchemaResponse, Error> {
  return useQuery<FormSchemaResponse, Error>({
    queryKey: formSchemaQueryKey(categoryId ?? ""),
    queryFn: () => getFormSchema(categoryId as string),
    enabled: categoryId !== null && categoryId !== "",
    staleTime: 60_000,
  });
}
